import React from 'react';
import { motion } from 'framer-motion';
import { FiPhone, FiMessageCircle, FiMail, FiMapPin } from 'react-icons/fi';
import { FaInstagram, FaLinkedin, FaFacebook } from 'react-icons/fa';

export default function Contact() {

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h1>Contact Our Chennai Construction Team</h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-6">
            Get in touch for your construction project needs across Chennai and surrounding areas
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            className="space-y-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div>
              <h3 className="text-2xl font-bold text-black mb-6">Connect With Us</h3>
              <p className="text-gray-600 text-lg mb-8">
                We're here to help with any questions about our services and to discuss your project needs.
              </p>
            </div>

            {/* Contact Methods */}
            <motion.div
              variants={itemVariants}
              className="flex gap-4 items-start p-6 rounded-lg border-2 border-gray-200 hover:border-black hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                <FiPhone className="text-white" size={20} />
              </div>
              <div>
                <h4 className="font-semibold text-black mb-1">Call Us</h4>
                <a href="tel:9841113286" className="text-gray-600 hover:text-black transition-colors">
                  9841113286
                </a>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="flex gap-4 items-start p-6 rounded-lg border-2 border-gray-200 hover:border-black hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                <FiMessageCircle className="text-white" size={20} />
              </div>
              <div>
                <h4 className="font-semibold text-black mb-1">WhatsApp</h4>
                <a href="https://wa.me/919150187138" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-black transition-colors">
                  9150187138
                </a>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="flex gap-4 items-start p-6 rounded-lg border-2 border-gray-200 hover:border-black hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                <FiMail className="text-white" size={20} />
              </div>
              <div>
                <h4 className="font-semibold text-black mb-1">Email</h4>
                <a href="mailto:hello@sankardesign.com" className="text-gray-600 hover:text-black transition-colors">
                  iyyappanramana122@gmail.com
                </a>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="flex gap-4 items-start p-6 rounded-lg border-2 border-gray-200 hover:border-black hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                <FiMapPin className="text-white" size={20} />
              </div>
              <div>
                <h4 className="font-semibold text-black mb-1">Office at Chennai</h4>
                <p className="text-gray-600">Ayanavaram, Chennai - 600023</p>
                <p className="text-gray-600 text-sm">Tamil Nadu, India</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Social Media */}
          <motion.div
            className="bg-gray-50 rounded-lg p-8 border-2 border-gray-200"
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold text-black mb-6">Follow Us on Social Media</h3>
              <p className="text-gray-600 mb-8">
                Stay connected and get inspired by our latest projects and design ideas.
              </p>
              <div className="flex justify-center space-x-8">
                <motion.a
                  href="https://www.instagram.com/sankar_construction1?utm_source=qr&igsh=NG80eHEyNjh0cHhr"
                  className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaInstagram size={28} />
                </motion.a>
                <motion.a
                  href="https://www.linkedin.com/in/iyyappan-s-3329253b9"
                  className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaLinkedin size={28} />
                </motion.a>
                <motion.a
                  href="https://www.facebook.com/profile.php?id=100093686070283&sfnsn=wa"
                  className="w-16 h-16 bg-blue-700 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaFacebook size={28} />
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
